import type { at } from '../generated/Beans';
import { PortletStoreApi } from '../App';
import type { LoadInitialStateParams } from '../communication/ajax';
import {
  loadInitialState,
  nextState,
  previousState,
  verifyGoogleReCaptcha
} from '../communication/ajax';
import type { LoadingStore } from 'ehs-react';
import { showError } from 'ehs-react';

import { create } from 'zustand';
import { produce } from 'immer';
import { convertToIsoDateString } from 'virtual-visit-common';
import { displayError } from '../utils/utils';
type ViewStateData = at.sense.portlets.saf.virtualvisitpatient.model.state.ViewStateData;
type Institution = at.sense.portlets.saf.virtualvisitpatient.model.Institution;
type AppointmentDataWrapper = at.sense.portlets.saf.virtualvisitpatient.model.AppointmentDataWrapper;

export interface ViewStateStore extends LoadingStore<LoadInitialStateParams> {
  devicePermissionAsked: boolean;
  selectedAppointment: string;
  recaptchaVerified: boolean;
  securityFormVerified: boolean;
  viewStateData?: ViewStateData;
  personalForm?: PersonalForm;
  setAppointmentMode: (appointmentMode: boolean) => void;
  setDevicePermissionAsked: (devicePermissionAsked: boolean) => void;
  setDeviceSelected: (deviceSelected: boolean) => void;
  setReconnectSessionMode: (reconnectMode: boolean) => void;
  setAppointmentId: (appointmentId: string) => void;
  setAccessCode: (accessCode: string) => void;
  setIdentificationNumber: (idNumber: string) => void;
  setFamilyName: (familyName: string) => void;
  nextState: () => Promise<void>;
  previousState: () => Promise<void>;
  selectInstitution: (ambulance: Institution) => Promise<void>;
  setEmailConsent: (consent: boolean) => void;
  setPatientPortalIdentifier: () => void;
  verifyGoogleRecaptcha: (token: string) => void;
  updatePatientData: (persForm: PersonalForm) => void;
  updateGuestData: (persForm: PersonalForm) => void;
  setPersonalForm: (persForm: PersonalForm) => void;
  setVVSecForm: (secDataForm: VirtualVisitSecForm) => void;
  updateSecurityForm: (loadTime: number) => void;
  getInitialPersonalFormValues: (params: {
    [index: string]: any;
  }) => PersonalForm;
  selectAppointment: (appointment: AppointmentDataWrapper) => void;
}

export interface PersonalForm {
  id: string | undefined;
  givenName: string | undefined;
  lastName: string | undefined;
  birthday: Date | null;
  email: string | undefined;
  emailConsent: boolean;
}

/**
 * Data for the honeypot.
 */
export interface VirtualVisitSecForm {
  firstName: string;
  lastName: string;
  email: string;
  period: number;
}

export const [useViewStateStore, ViewStateStoreApi] = create<ViewStateStore>(
  (set, get) => ({
    devicePermissionAsked: false,
    selectedAppointment: '',
    loading: false,
    wasLoaded: false,
    recaptchaVerified: false,
    securityFormVerified: true,
    personalForm: undefined,
    load: async (): Promise<void> => {
      console.log('load ');
      const userAgent = navigator.userAgent;
      const resp = await loadInitialState({
        userAgent: userAgent,
        url: PortletStoreApi.getState().url
      });
      const storedQueueId = sessionStorage.getItem('queueId');
      const storedEnteredIdentificationNumber = sessionStorage.getItem(
        'enteredIdentificationNumber'
      );
      const storedEnteredFamilyName = sessionStorage.getItem(
        'enteredFamilyName'
      );
      let storedInstitution: Institution | undefined = undefined;
      const storedInstitutionStr = sessionStorage.getItem(
        'selectedInstitution'
      );

      const visitId = sessionStorage.getItem('visitId');
      const urlToken = sessionStorage.getItem('urlToken');
      const rejoinCode = sessionStorage.getItem('rejoinCode');

      if (storedInstitutionStr) {
        storedInstitution = JSON.parse(storedInstitutionStr) as Institution;
      }
      if (storedQueueId) {
        resp.data.data.queueId = storedQueueId;
      }
      if (storedEnteredIdentificationNumber) {
        resp.data.data.enteredRegistrationData.identificationNumber = storedEnteredIdentificationNumber;
      }
      if (storedEnteredFamilyName) {
        resp.data.data.enteredRegistrationData.familyName = storedEnteredFamilyName;
      }
      if (storedInstitution) {
        resp.data.data.selectedInstitution = storedInstitution;
      }
      if (visitId) {
        resp.data.data.visitId = visitId;
      }
      if (urlToken && rejoinCode) {
        resp.data.data.guestData = {
          email: '',
          firstName: '',
          lastName: '',
          urlToken: urlToken,
          rejoinCode: rejoinCode
        };
      }
      set(state => (state.viewStateData = resp.data.data));
    },
    setAppointmentMode: (appointmentMode: boolean): void => {
      console.log('setAppointmentMode', appointmentMode);
      set(
        produce(state => {
          state.viewStateData.appointmentMode = appointmentMode;
        })
      );
    },
    setDevicePermissionAsked: (devicePermissionAsked: boolean): void => {
      console.log('setDevicePermissionAsked', devicePermissionAsked);
      set(
        produce(state => {
          state.viewStateData.devicePermissionAsked = devicePermissionAsked;
        })
      );
    },
    setDeviceSelected: (deviceSelected: boolean): void => {
      console.log('setDeviceSelected', deviceSelected);
      set(
        produce(state => {
          state.viewStateData.deviceSelected = deviceSelected;
        })
      );
    },
    setReconnectSessionMode: (reconnectMode: boolean): void => {
      console.log('setAppointmentMode', reconnectMode);
      set(
        produce(state => {
          state.viewStateData.reconnectSessionMode = reconnectMode;
        })
      );
    },
    setAppointmentId: (appointmentId: string): void => {
      console.log('setAppointmentId', appointmentId);
      set(
        produce(state => {
          state.viewStateData.appointmentId = appointmentId;
        })
      );
    },
    setAccessCode: (accessCode: string): void => {
      console.log('setAccessCode', accessCode);
      set(
        produce(state => {
          state.viewStateData.queueId = accessCode;
        })
      );
    },
    setIdentificationNumber: (idNumber: string): void => {
      console.log('setIdentificationNumber', idNumber);
      if (get().viewStateData?.patientData) {
        set(
          produce(state => {
            state.viewStateData.patientData.patientIdentificator = idNumber;
          })
        );
      } else {
        set(
          produce(state => {
            state.viewStateData.patientData = {
              patientIdentificator: idNumber
            };
          })
        );
      }
      set(
        produce(state => {
          state.viewStateData.enteredRegistrationData.identificationNumber = idNumber;
        })
      );
    },
    setFamilyName: (familyName: string): void => {
      console.log('setFamilyName', familyName);
      if (get().viewStateData?.patientData) {
        set(
          produce(state => {
            state.viewStateData.patientData.familyName = familyName;
          })
        );
      } else {
        set(
          produce(state => {
            state.viewStateData.patientData = {
              familyName: familyName
            };
          })
        );
      }
      set(
        produce(state => {
          state.viewStateData.enteredRegistrationData.familyName = familyName;
        })
      );
    },
    nextState: async (): Promise<void> => {
      console.log('nextState');
      try {
        const vsd = get().viewStateData;
        console.log('send view state date', vsd);
        if (vsd) {
          set(
            produce(state => {
              state.loading = true;
            })
          );
          const resp = await nextState({
            url: PortletStoreApi.getState().url,
            stateData: vsd
          });
          set(
            produce(state => {
              state.viewStateData = resp.data.data;
            })
          );
          if (resp.data.errors) {
            if (resp.data.data.state === 'DISCLAIMER') {
              set(state => (state.securityFormVerified = false));
            }
            displayError(resp.data.errors);
          }
        }
      } catch (e) {
        console.log('nextState error', e);
        showError(e);
      } finally {
        set(
          produce(state => {
            state.loading = false;
          })
        );
      }
    },
    previousState: async (): Promise<void> => {
      console.log('previousState');
      try {
        set(
          produce(state => {
            state.loading = true;
          })
        );
        const resp = await previousState({
          url: PortletStoreApi.getState().url
        });
        set(
          produce(state => {
            state.viewStateData = resp.data.data;
          })
        );
        if (resp.data.errors) {
          displayError(resp.data.errors);
        }
      } catch (e) {
        console.log('previousState error', e);
        showError(e);
      } finally {
        set(
          produce(state => {
            state.loading = false;
          })
        );
      }
    },
    selectInstitution: async (inst: Institution): Promise<void> => {
      console.log('selectInstitution', inst);
      set(
        produce(state => {
          state.viewStateData.selectedInstitution = inst;
        })
      );
      get().nextState();
    },
    setEmailConsent: (consent: boolean): void => {
      set(
        produce(state => {
          state.viewStateData.patientData.emailConsentState = consent;
        })
      );
    },
    setPatientPortalIdentifier: (): void => {
      set(
        produce(state => {
          state.viewStateData.patientData.patientPortal = true;
        })
      );
    },
    updatePatientData: (persForm: PersonalForm): void => {
      console.log('updatePatientData', persForm);
      set(
        produce(state => {
          if (!state.viewStateData.patientData) {
            state.viewStateData.patientData = {
              birthDate: '',
              email: '',
              emailConsentState: false,
              familyName: '',
              givenName: '',
              locale: '',
              patientIdentificator: '',
              patientPortal: false
            };
          }
          state.viewStateData.patientData.patientIdentificator = persForm.id
            ? persForm.id
            : undefined;
          state.viewStateData.patientData.givenName = persForm.givenName
            ? persForm.givenName
            : undefined;
          state.viewStateData.patientData.familyName = persForm.lastName
            ? persForm.lastName
            : undefined;
          state.viewStateData.patientData.birthDate = persForm.birthday
            ? convertToIsoDateString(persForm.birthday)
            : undefined;
          state.viewStateData.patientData.email = persForm.email
            ? persForm.email
            : undefined;
          state.viewStateData.patientData.emailConsentState =
            persForm.emailConsent;
        })
      );
      console.log('after set patient', get().viewStateData?.patientData);
    },
    updateGuestData: (persForm: PersonalForm): void => {
      console.log('update guest data', persForm);
      set(
        produce(state => {
          if (!state.viewStateData.guestData) {
            state.viewStateData.guestData = {
              email: '',
              firstName: '',
              lastName: ''
            };
          }
          state.viewStateData.guestData.firstName = persForm.givenName
            ? persForm.givenName
            : undefined;
          state.viewStateData.guestData.lastName = persForm.lastName
            ? persForm.lastName
            : undefined;
          state.viewStateData.guestData.email = persForm.email
            ? persForm.email
            : undefined;
        })
      );
      console.log('after set guest', get().viewStateData?.guestData);
    },
    setPersonalForm: (persForm: PersonalForm): void => {
      set(
        produce(state => {
          state.personalForm = persForm;
        })
      );
    },
    verifyGoogleRecaptcha: async (token: string): Promise<void> => {
      console.log('verifyGoogleRecaptcha');
      const resp = await verifyGoogleReCaptcha({
        url: PortletStoreApi.getState().url,
        recaptchaResponse: token
      });
      set(state => (state.recaptchaVerified = resp.data));
    },
    setVVSecForm: (secDataForm: VirtualVisitSecForm): void => {
      set(
        produce(state => {
          state.viewStateData.securityFormData = secDataForm;
        })
      );
    },
    updateSecurityForm: (creationTime: number): void => {
      set(
        produce(state => {
          if (!state.viewStateData.securityFormData) {
            // when sec for is not filled, initialize
            state.viewStateData.securityFormData = {
              firstName: '',
              lastName: '',
              email: '',
              period: creationTime
            };
          }
          //set time taken to fill the form
          state.viewStateData.securityFormData.period =
            new Date().getTime() - state.viewStateData.securityFormData.period;
        })
      );
    },
    getInitialPersonalFormValues: (params: {
      [index: string]: any;
    }): PersonalForm => {
      if (get().personalForm) {
        return get().personalForm as PersonalForm;
      }

      const createDate = (date: string): Date | null => {
        try {
          const theDate = new Date(params.patientBirthdate + 'T00:00:00'); //the time is added to treat the date as it is in the local time zone to avoid display issues
          if (theDate.toString() === 'Invalid Date') {
            return null;
          } else {
            return theDate;
          }
        } catch (e) {
          console.log('invalid date format -> ignore');
          return null;
        }
      };

      return {
        id: params.patientId || '',
        givenName: params.patientFirstName || '',
        lastName: params.patientLastName || '',
        birthday: params.patientBirthdate
          ? createDate(params.patientBirthdate)
          : null,

        email: params.patientEmail || '',
        emailConsent: params.sendEmailConfirmation
      };
    },
    selectAppointment: (appointment: AppointmentDataWrapper): void => {
      console.log('selectAppointment', appointment);
      set(
        produce(state => {
          state.viewStateData.selectedAppointmentIdentifier =
            appointment.appointmentId;
        })
      );
    }
  })
);
